/* ============================================================
   CART DRAWER — Hansen Nord
   Ficheiro : assets/hn-cart.js
   ============================================================ */

'use strict';

const HNCart = (() => {

  /* ── State ── */
  let cart  = null;
  let busy  = false;

  /* ── Formatters ── */
  const fmtPrice = cents =>
    (cents / 100).toFixed(2).replace('.', ',') + ' kr';

  /* ── DOM helpers ── */
  const $ = id => document.getElementById(id);

  /* ── Open / Close ── */
  function open() {
    $('hn-cart-drawer').classList.add('is-open');
    $('hn-cart-overlay').classList.add('is-visible');
    document.body.classList.add('hn-cart-no-scroll');
    fetchCart();
  }

  function close() {
    $('hn-cart-drawer').classList.remove('is-open');
    $('hn-cart-overlay').classList.remove('is-visible');
    document.body.classList.remove('hn-cart-no-scroll');
  }

  /* ── Fetch cart ── */
  async function fetchCart() {
    try {
      const res = await fetch('/cart.js');
      cart = await res.json();
      render();
    } catch (e) {
      console.error('[HNCart] fetchCart error:', e);
    }
  }

  /* ── Update item quantity ── */
  async function updateItem(key, qty) {
    if (busy) return;
    busy = true;
    // Visual feedback
    const itemEl = document.querySelector(`.hn-cart__item[data-key="${key}"]`);
    if (itemEl) itemEl.classList.add('hn-cart__item--loading');
    try {
      await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: key, quantity: qty })
      });
      await fetchCart();
    } catch (e) {
      console.error('[HNCart] updateItem error:', e);
    } finally {
      busy = false;
    }
  }

  /* ── Add item ── */
  async function addItem(variantId, qty) {
    if (busy) return;
    busy = true;
    try {
      await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: parseInt(variantId), quantity: qty })
      });
      await fetchCart();
    } catch (e) {
      console.error('[HNCart] addItem error:', e);
    } finally {
      busy = false;
    }
  }

  /* ── Render ── */
  function render() {
    if (!cart) return;
    renderCount();
    renderProgress();
    renderItems();
    renderTotals();
    renderUpsell();
  }

  /* Count badge in header */
  function renderCount() {
    const count = cart.item_count;
    $('hn-cart-count').textContent = count;
    // Update any native cart count badges in the theme
    document.querySelectorAll('[data-cart-count], .cart-count, .cart__count').forEach(el => {
      el.textContent = count;
    });
  }

  /* Progress bar — 3 states */
  function renderProgress() {
    const count = cart.item_count;
    const fill  = $('hn-cart-progress-fill');
    const msg   = $('hn-cart-progress-msg');

    const pct = Math.min(Math.round((count / 3) * 100), 100);
    fill.style.width = pct + '%';

    if (count === 0) {
      msg.innerHTML = 'Tilføj <strong>3 smykker</strong> og få det billigste gratis';
    } else if (count === 1) {
      msg.innerHTML = 'Tilføj <strong>2 smykker mere</strong> og få det 3. gratis!';
    } else if (count === 2) {
      msg.innerHTML = 'Du mangler kun <strong>1 smykke</strong> — det 3. er på vores regning!';
    } else {
      msg.innerHTML = '<span class="hn-cart__promo-active">✓ Det 3. smykke er <strong>GRATIS!</strong></span>';
    }
  }

  /* Items list */
  function renderItems() {
    const container = $('hn-cart-items');

    if (!cart.items.length) {
      container.innerHTML = '<p class="hn-cart__empty">Din kurv er tom</p>';
      return;
    }

    /* Identify cheapest unit when 3+ items qualify */
    const sortedByPrice = [...cart.items].sort((a, b) => a.price - b.price);
    const freeKey = cart.item_count >= 3 ? sortedByPrice[0].key : null;

    container.innerHTML = cart.items.map(item => {
      const isFree   = item.key === freeKey;
      const imgRaw   = item.image;
      const imgSrc   = (typeof imgRaw === 'string' ? imgRaw : imgRaw?.src) ||
                       item.featured_image?.url || '';
      const hasCompare = item.original_price && item.original_price > item.price;

      return `
<div class="hn-cart__item" data-key="${item.key}">

  <div class="hn-cart__item-img-wrap">
    ${imgSrc
      ? `<img src="${imgSrc}" alt="${escHtml(item.title)}" class="hn-cart__item-img" loading="lazy">`
      : '<div class="hn-cart__item-img" style="background:#f0ede9;border-radius:6px"></div>'}
    ${isFree ? '<span class="hn-cart__free-badge">GRATIS</span>' : ''}
  </div>

  <div class="hn-cart__item-info">
    <p class="hn-cart__item-title">${escHtml(item.product_title)}</p>
    ${item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="hn-cart__item-variant">${escHtml(item.variant_title)}</p>`
      : ''}

    <div class="hn-cart__item-price-row">
      <div class="hn-cart__item-price">
        ${isFree
          ? `<span class="hn-cart__item-original">${fmtPrice(item.price)}</span>
             <span class="hn-cart__item-free">Gratis</span>`
          : hasCompare
            ? `<span class="hn-cart__item-original">${fmtPrice(item.original_price)}</span>
               <span class="hn-cart__item-sale">${fmtPrice(item.price)}</span>`
            : `<span>${fmtPrice(item.price)}</span>`
        }
      </div>

      <div class="hn-cart__item-qty">
        <button class="hn-cart__qty-btn"
                data-action="decrease"
                data-key="${item.key}"
                aria-label="Mindsk antal">−</button>
        <span>${item.quantity}</span>
        <button class="hn-cart__qty-btn"
                data-action="increase"
                data-key="${item.key}"
                aria-label="Forøg antal">+</button>
      </div>
    </div>
  </div>

  <button class="hn-cart__item-remove" data-key="${item.key}" aria-label="Fjern produkt">
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
         viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6M14 11v6"/>
    </svg>
  </button>

</div>`;
    }).join('');

    /* Bind qty + remove events */
    container.querySelectorAll('.hn-cart__qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const key    = btn.dataset.key;
        const action = btn.dataset.action;
        const item   = cart.items.find(i => i.key === key);
        if (!item) return;
        const newQty = action === 'increase' ? item.quantity + 1 : item.quantity - 1;
        updateItem(key, newQty);
      });
    });

    container.querySelectorAll('.hn-cart__item-remove').forEach(btn => {
      btn.addEventListener('click', () => updateItem(btn.dataset.key, 0));
    });
  }

  /* Totals */
  function renderTotals() {
    const originalTotalEl = $('hn-cart-original-total');
    const saleTotalEl     = $('hn-cart-sale-total');
    const savingsMsgEl    = $('hn-cart-savings-msg');

    if (!cart.items.length) {
      originalTotalEl.style.display = 'none';
      saleTotalEl.textContent = '0,00 kr';
      savingsMsgEl.style.display = 'none';
      return;
    }

    /* Sum of compare_at (original) prices */
    const originalTotal = cart.items.reduce(
      (sum, item) => sum + ((item.original_price || item.price) * item.quantity),
      0
    );

    if (cart.item_count >= 3) {
      /* Find cheapest unit */
      const sortedByPrice  = [...cart.items].sort((a, b) => a.price - b.price);
      const cheapestCents  = sortedByPrice[0].price;
      const discountedTotal = cart.total_price - cheapestCents < 0
        ? 0
        : cart.total_price - cheapestCents;

      /* Show original (compare_at) crossed out if it's higher */
      if (originalTotal > cart.total_price) {
        originalTotalEl.textContent = fmtPrice(originalTotal);
        originalTotalEl.style.display = 'inline';
      } else {
        /* Fallback: show regular total crossed out */
        originalTotalEl.textContent = fmtPrice(cart.total_price);
        originalTotalEl.style.display = 'inline';
      }

      saleTotalEl.textContent = fmtPrice(discountedTotal);

      savingsMsgEl.textContent =
        `🎉 Du sparer ${fmtPrice(cheapestCents)} på dit 3. smykke`;
      savingsMsgEl.style.display = 'block';

    } else {
      /* No buy-2-get-3 offer active */
      if (originalTotal > cart.total_price) {
        originalTotalEl.textContent = fmtPrice(originalTotal);
        originalTotalEl.style.display = 'inline';
      } else {
        originalTotalEl.style.display = 'none';
      }
      saleTotalEl.textContent = fmtPrice(cart.total_price);
      savingsMsgEl.style.display = 'none';
    }
  }

  /* Upsell carousel */
  async function renderUpsell() {
    const upsellEl = $('hn-cart-upsell');
    const track    = $('hn-cart-upsell-track');

    if (!cart.items.length) {
      upsellEl.style.display = 'none';
      return;
    }

    try {
      const productId = cart.items[0].product_id;
      const res  = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=8&intent=related`
      );
      const data = await res.json();

      /* Filter out items already in cart */
      const cartIds  = cart.items.map(i => i.product_id);
      const products = (data.products || []).filter(p => !cartIds.includes(p.id));

      if (!products.length) {
        upsellEl.style.display = 'none';
        return;
      }

      track.innerHTML = products.slice(0, 5).map(p => {
        const img    = p.featured_image;
        const imgSrc = (typeof img === 'string' ? img : img?.src) ||
                       p.images?.[0]?.src || '';
        const priceC   = p.price;
        const compareC = p.compare_at_price;

        return `
<div class="hn-cart__upsell-item">
  <a href="${p.url}" class="hn-cart__upsell-img-wrap">
    ${imgSrc
      ? `<img src="${imgSrc}" alt="${escHtml(p.title)}" class="hn-cart__upsell-img" loading="lazy">`
      : ''}
  </a>
  <p class="hn-cart__upsell-name">${escHtml(p.title)}</p>
  <div class="hn-cart__upsell-price">
    ${compareC && compareC > priceC
      ? `<span class="hn-cart__upsell-compare">${fmtPrice(compareC)}</span>`
      : ''}
    <span class="hn-cart__upsell-current">${fmtPrice(priceC)}</span>
  </div>
  <button class="hn-cart__upsell-add" data-variant-id="${p.variants[0].id}">
    + Tilføj
  </button>
</div>`;
      }).join('');

      /* Bind upsell add-to-cart buttons */
      track.querySelectorAll('.hn-cart__upsell-add').forEach(btn => {
        btn.addEventListener('click', async () => {
          const origText = btn.textContent;
          btn.textContent = '…';
          btn.disabled = true;
          await addItem(btn.dataset.variantId, 1);
          btn.textContent = '✓ Tilføjet';
          setTimeout(() => {
            btn.textContent = origText;
            btn.disabled = false;
          }, 2500);
        });
      });

      upsellEl.style.display = 'block';

    } catch (e) {
      console.error('[HNCart] renderUpsell error:', e);
      upsellEl.style.display = 'none';
    }
  }

  /* ── Intercept add-to-cart forms ── */
  function interceptForms() {
    document.addEventListener('submit', async e => {
      const form = e.target;
      if (!form.matches('[action="/cart/add"]')) return;
      e.preventDefault();

      const btn = form.querySelector('[type="submit"], button:not([type])');
      const orig = btn?.textContent;
      if (btn) { btn.textContent = 'Tilføjer…'; btn.disabled = true; }

      try {
        const res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Accept': 'application/json' },
          body:    new FormData(form)
        });
        if (res.ok) {
          await fetchCart();
          open();
        }
      } catch (err) {
        console.error('[HNCart] add to cart error:', err);
      } finally {
        if (btn) { btn.textContent = orig; btn.disabled = false; }
      }
    });
  }

  /* ── Intercept cart icon clicks ── */
  function interceptCartLinks() {
    document.addEventListener('click', e => {
      const link = e.target.closest('a[href="/cart"], a[href*="/cart"]');
      if (!link) return;
      /* Avoid intercepting checkout links */
      if (link.href?.includes('/checkout')) return;
      e.preventDefault();
      open();
    });
  }

  /* ── XSS helper ── */
  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Init ── */
  function init() {
    /* Close button */
    const closeBtn = $('hn-cart-close');
    const overlay  = $('hn-cart-overlay');
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay)  overlay.addEventListener('click', close);

    /* Escape key */
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') close();
    });

    interceptCartLinks();
    interceptForms();
    fetchCart(); /* preload cart count on page load */
  }

  /* ── Public API ── */
  return { init, open, close, fetchCart };

})();

/* Boot */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => HNCart.init());
} else {
  HNCart.init();
}

/* ── Fetch interceptor (fallback para temas com JS próprio) ── */
(function() {
  const _fetch = window.fetch;
  window.fetch = function(input, init) {
    return _fetch.apply(this, arguments).then(response => {
      const url = (typeof input === 'string' ? input : input?.url) || '';
      if (url.includes('/cart/add')) {
        response.clone().json()
          .then(() => {
            HNCart.fetchCart().then(() => HNCart.open());
          })
          .catch(() => {});
      }
      return response;
    });
  };
})();